import { Component } from 'react';
import { connect } from 'react-redux';
import { ReactSVG } from 'react-svg';
import { FormattedMessage } from 'react-intl';

import companyIconSrc from '../assets/images/usage-types/corporate.svg';
import CompanyChooser from './registration/CompanyChooser';
import { getBrandCompanies, getUserProfiles } from '../actions/user';
import {
  availableBrandCompaniesSelector,
  brandCompaniesSelector,
  brandIdSelector,
  mapProfilesToCompanyIds,
  profileCompanyIdsSelector,
  userIdSelector,
  userProfilesSelector
} from '../helpers/selectors';
import {
  chooserItemsForProfileFacts,
  filterUnlinkedCompanyItems,
  normalizePublicCompaniesResponse
} from '../helpers/ssoRegistrationFlow';
import { navigateToCreationPage } from '../utils/utils';

/**
 * Company picker of the post-Keycloak cascade: the brand's public super-companies
 * as logo tiles. A single remaining company skips the picker straight into the
 * creation form; an empty list degrades to invite-only or "registration
 * unavailable" (CompanyChooser owns those states).
 */
class SelectBrandCompany extends Component {
  constructor(props) {
    super(props);
    // Until the profile-facts attempt finishes, the chooser is shown its
    // loading state instead of a potentially unfiltered list (see
    // chooserItemsForProfileFacts for the full contract).
    this.state = { profileFactsReady: false };
    this.renderCompanyList = this.renderCompanyList.bind(this);
  }

  componentDidMount() {
    this.ensureProfilesLoaded().then((profileFacts) => {
      this.setState({ profileFactsReady: true });
      this.resolveCompanies(profileFacts);
    });
  }

  // The add-profile entry can land here on a hard refresh with profiles not yet
  // loaded: the already-linked filter would see no ids and the single-company
  // shortcut could drive the user into a company he already holds a profile in.
  // So the linked-company facts are settled first, and the shortcut only runs
  // once they actually are (an anonymous registrant has none by definition).
  ensureProfilesLoaded() {
    const { userId, profiles, profileCompanyIds } = this.props;

    if (!userId || profiles) {
      return Promise.resolve({ settled: true, profileCompanyIds });
    }

    return this.props.getUserProfiles().then((resp) => {
      const settled = resp.type === 'user/GET_PROFILES_SUCCESS';

      return {
        settled,
        // fresh ids straight from the response - connected props have not
        // re-rendered within this tick
        profileCompanyIds: settled ? mapProfilesToCompanyIds(resp.payload.data) : null
      };
    });
  }

  resolveCompanies(profileFacts) {
    const { props: p } = this;

    // App may already have fetched the list for the fallback cascade; only a
    // mount with nothing loaded fetches. The shortcut reads the raw list and
    // filters with the freshly settled ids rather than the store selector -
    // connected props do not update within this tick.
    if (p.rawList !== null) {
      this.shortcutSingleCompany(p.rawList, profileFacts);
      return;
    }

    p.getBrandCompanies(p.brandId).then((resp) => {
      if (resp.type !== 'user/GET_BRAND_COMPANIES_SUCCESS') return;

      this.shortcutSingleCompany(
        normalizePublicCompaniesResponse(resp.payload.data),
        profileFacts
      );
    });
  }

  // Mobile getDefaultCompany cascade: exactly one company left means there is
  // nothing to pick - straight to the creation form. `replace` keeps the picker
  // out of the history, so Back cannot land on a page that would only redirect
  // forward again. Never taken on unsettled profile facts - the picker stays.
  shortcutSingleCompany(companies, { settled, profileCompanyIds }) {
    if (!settled) return;

    const available = filterUnlinkedCompanyItems(
      companies,
      profileCompanyIds,
      (company) => company.id
    );

    if (available?.length === 1) navigateToCreationPage(available[0].id, true);
  }

  renderCompanyList(items) {
    return (
      <div className="sc-list">
        {items.map((item) => (
          <button
            key={item.id}
            className="sc-item"
            onClick={() => navigateToCreationPage(item.id)}
          >
            <span className="sc-img-container">
              <img alt="company-logo" src={item.logoUrl} />
            </span>
            <span className="sc-company-name">{item.name}</span>
          </button>
        ))}
      </div>
    );
  }

  render() {
    return (
      <div className="select-brand-company">
        <div className="sc-wrap">
          <div className="sc-entry-icon">
            <ReactSVG wrapper="span" className="react-svg" src={companyIconSrc} />
            <FormattedMessage id="service.usage.corporate" />
          </div>
          <div className="sc-inner">
            <CompanyChooser
              items={chooserItemsForProfileFacts(
                this.state.profileFactsReady,
                this.props.list
              )}
              titleClassName="sc-title-msg"
              renderList={this.renderCompanyList}
            />
          </div>
        </div>
      </div>
    );
  }
}

function mapStateToProps(state) {
  return {
    brandId: brandIdSelector(state),
    list: availableBrandCompaniesSelector(state),
    rawList: brandCompaniesSelector(state),
    profileCompanyIds: profileCompanyIdsSelector(state),
    profiles: userProfilesSelector(state),
    userId: userIdSelector(state)
  };
}

const mapDispatchToProps = { getBrandCompanies, getUserProfiles };

export default connect(mapStateToProps, mapDispatchToProps)(SelectBrandCompany);
